import { colors, s8 } from '@ardoq/design-tokens';
import styled from 'styled-components';
import { PlainTextPopover, WithUncontrolledPopover } from '@ardoq/popovers';
import { formatNumber } from '@ardoq/locale';
import { FlexBox } from '@ardoq/layout';

export type ProgressDecoratorProps = {
  value: number;
  of?: number;
  unit?: string;
  color?: string;
  width?: number;
  label: string;
};

const ProgressValue = styled.span`
  min-width: 36px; /* width of the longest value '100%' */
  text-align: right;
`;

const ProgressWrapper = styled.div<{ $width?: number }>`
  position: relative;
  height: ${s8};
  border-radius: ${s8};
  background: ${colors.surfaceDefaultSubtle};
  width: ${({ $width = 100 }) => `${$width}px`};
`;

export const Progress = styled.div.attrs<
  Pick<ProgressDecoratorProps, 'value' | 'color'>
>(({ value, color }) => ({
  style: {
    width: `${value}%`,
    backgroundColor: color || colors.blue600,
  },
}))<Pick<ProgressDecoratorProps, 'value'>>`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: ${s8};
  height: 100%;
  min-width: 0;
  max-width: 100%;
`;

export const ProgressDecorator = ({
  value = 0,
  of,
  color,
  unit = '',
  width,
  label,
}: ProgressDecoratorProps) => {
  const percentValue = Math.round((value / (of || 100)) * 100);
  const valueText =
    of !== undefined
      ? `${formatNumber(value)} of ${formatNumber(of)} ${unit}`
      : `${percentValue}%`;
  return (
    <FlexBox gap="small" align="center">
      <ProgressValue>{percentValue}%</ProgressValue>
      <ProgressBar
        percentValue={percentValue}
        color={color}
        width={width}
        popoverText={of !== undefined ? valueText : undefined}
        progressbar={{
          label,
          valueNow: of !== undefined ? value : percentValue,
          valueMax: of !== undefined ? of : 100,
          valueText,
        }}
      />
    </FlexBox>
  );
};

type ProgressBarProps = {
  percentValue: number;
  color?: string;
  popoverText?: string;
  width?: number;
  progressbar: {
    label: string;
    valueNow: number;
    valueMax: number;
    valueText: string;
  };
};

export const ProgressBar = ({
  percentValue,
  color,
  popoverText,
  width,
  progressbar,
}: ProgressBarProps) => (
  <WithUncontrolledPopover
    content={
      popoverText ? <PlainTextPopover>{popoverText}</PlainTextPopover> : null
    }
  >
    <ProgressWrapper
      $width={width}
      role="progressbar"
      aria-label={progressbar.label}
      aria-valuemin={0}
      aria-valuenow={progressbar.valueNow}
      aria-valuemax={progressbar.valueMax}
      aria-valuetext={progressbar.valueText}
    >
      <Progress value={percentValue} color={color} />
    </ProgressWrapper>
  </WithUncontrolledPopover>
);
